import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { logger } from "./logger";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get("/auth/me")
      .then((r) => alive && setUser(r.data))
      .catch(() => alive && setUser(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const login = async (email, password) => {
    // Server sets HttpOnly access + refresh cookies. We do NOT persist any token client-side.
    const res = await api.post("/auth/login", { email, password });
    if (res.data?.requires_2fa) {
      // Caller is responsible for routing to the 2FA challenge page.
      return { requires_2fa: true, challenge_token: res.data.challenge_token, email: res.data.user_email };
    }
    setUser(res.data?.user);
    return res.data?.user;
  };

  const verify2fa = async (challenge_token, code) => {
    const res = await api.post("/auth/2fa/verify", { challenge_token, code });
    setUser(res.data?.user);
    return res.data?.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Logout failures (network/already-expired token) are non-fatal —
      // server-side cookies still expire; we clear local state below.
      logger.warn("Logout request failed:", e?.message || e);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, verify2fa }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
